import { EPermissions } from '@auth/login/data/login.model';

export interface Link {
  name: string;
  route: string;
  routeParams?: { [key: string]: string };
  testId: string;
  /**
   * Name of the GrowthBook boolean flag controlling this link's visibility.
   * Every menu link is controlled by GrowthBook (fail-closed):
   *  - flag missing in the GrowthBook payload → link hidden
   *  - flag = `true`                           → link shown
   *  - flag = `false`                          → link hidden
   * For now the value equals `testId`.
   */
  growthbookFlagId: string;
  icon?: string;
  disabled?: EPermissions[];
  hidden?: EPermissions[];
  checkIfInternalRole?: boolean;
  openInBlank?: boolean;
}

export interface Section {
  name: string;
  route: string;
  testId: string;
  /**
   * Name of the GrowthBook boolean flag controlling the whole section
   * (parent menu item). Fail-closed like links:
   *  - flag missing in the GrowthBook payload → section (incl. all links) hidden
   *  - flag = `true`                           → section shown (if ≥ 1 link visible)
   *  - flag = `false`                          → section (incl. all links) hidden
   * For now the value equals `testId`.
   */
  growthbookFlagId: string;
  icon: string;
  hidden?: EPermissions[];
  links: Link[];
}

export type MenuPoints = {
  sections: Section[];
};
export const MENU_POINTS: MenuPoints = {
  sections: [
    {
      name: 'HEADER.MONITOR',
      route: '/monitor',
      testId: 'header-menu-monitor',
      growthbookFlagId: 'header-menu-monitor',
      icon: 'activity-outlined',
      links: [
        {
          route: '/monitor/dashboard',
          name: 'HEADER.DASHBOARD',
          testId: 'header-menu-monitor-dashboard',
          growthbookFlagId: 'header-menu-monitor-dashboard',
          icon: 'activity-dashboard-outlined',
        },
        {
          route: '/monitor/chargingInfrastructure',
          routeParams: { activeTab: '0' },
          name: 'HEADER.CHARG_INFRASTR',
          testId: 'header-menu-monitor-charging-infrastructure',
          growthbookFlagId: 'header-menu-monitor-charging-infrastructure',
          icon: 'eoperate-ic-charging-infrastructure',
          disabled: [EPermissions.VIEW_CHARGINGSTATION_MASTERDATA],
        },
        {
          route: '/monitor/chargeRecords',
          routeParams: { activeTab: '0' },
          name: 'HEADER.LOAD_DATA_CHARG_INF',
          testId: 'header-menu-monitor-charge-records',
          growthbookFlagId: 'header-menu-monitor-charge-records',
          icon: 'eoperate-ic-charging-records',
          disabled: [EPermissions.VIEW_CHARGINGDATA_CPO, EPermissions.VIEW_CHARGINGDATA_EMP],
        },
        {
          route: '/monitor/alerting',
          name: 'HEADER.ALERTING',
          testId: 'header-menu-monitor-alerting',
          growthbookFlagId: 'header-menu-monitor-alerting',
          icon: 'eoperate-ic-notifications',
          disabled: [EPermissions.VIEW_ALERTING_CLIENT],
        },
        {
          route: '/monitor/reports',
          name: 'HEADER.QUICKSIGHT',
          testId: 'header-menu-monitor-quicksight',
          growthbookFlagId: 'header-menu-monitor-quicksight',
          icon: 'eoperate-ic-graph',
          disabled: [EPermissions.VIEW_QUICKSIGHT_REPORTS],
        },
        {
          route: '/monitor/activationOverview',
          routeParams: { activeTab: '0' },
          name: 'HEADER.UNLOCK_MONITOR',
          testId: 'header-menu-monitor-activation-overview',
          growthbookFlagId: 'header-menu-monitor-activation-overview',
          icon: 'eoperate-ic-activity-overview',
          disabled: [EPermissions.VIEW_ACTIVATIONOVERVIEW],
          hidden: [EPermissions.VIEW_ACTIVATIONOVERVIEW],
        },
      ],
    },
    {
      name: 'HEADER.MANAGE',
      route: '/manage',
      testId: 'header-menu-manage',
      growthbookFlagId: 'header-menu-manage',
      icon: 'eoperate-ic-manage',
      hidden: [
        EPermissions.VIEW_OPENINGTIMES,
        EPermissions.VIEW_LOADMANAGEMENT,
        EPermissions.VIEW_LOCAL_LOADMANAGEMENT,
        EPermissions.VIEW_PROFILES,
        EPermissions.VIEW_AID,
        EPermissions.VIEW_EMAID_GROUPS,
        EPermissions.VIEW_LEGACY_EMAID,
        EPermissions.VIEW_VOUCHER_CPO,
        EPermissions.VIEW_FIRMWARE,
        EPermissions.VIEW_SITES,
      ],
      links: [
        {
          route: '/manage/openingHours',
          name: 'HEADER.MANAGE_OPENING_HOURS',
          testId: 'header-menu-manage-opening-hours',
          growthbookFlagId: 'header-menu-manage-opening-hours',
          icon: 'eoperate-ic-opening-hours',
          disabled: [EPermissions.VIEW_OPENINGTIMES],
        },
        {
          route: '/manage/loadManagement',
          routeParams: { activeTab: '0' },
          name: 'HEADER.LOAD_MANAGEMENT',
          testId: 'header-menu-manage-load-management',
          growthbookFlagId: 'header-menu-manage-new-load-management',
          icon: 'eoperate-ic-load-management',
          disabled: [EPermissions.VIEW_LOADMANAGEMENT, EPermissions.VIEW_LOCAL_LOADMANAGEMENT],
        },
        {
          route: '/manage/ocppParameters',
          name: 'HEADER.OCPP_PARAMETERS',
          testId: 'header-menu-manage-ocpp-parameters',
          growthbookFlagId: 'header-menu-manage-ocpp-parameters',
          icon: 'ic-ocpp-parameters-outlined',
          hidden: [EPermissions.VIEW_OCPP_MASS_CONFIGURATION],
        },
        {
          hidden: [EPermissions.VIEW_PROFILES],
          route: '/manage/basicProfiles',
          name: 'HEADER.BASIC_PROFILES',
          testId: 'header-menu-manage-basic-profiles',
          growthbookFlagId: 'header-menu-manage-basic-profiles',
          icon: 'eoperate-ic-profiles',
        },
        {
          route: '/manage/accessControl',
          routeParams: { activeTab: '0' },
          name: 'HEADER.ACCESS_CONTROL',
          testId: 'header-menu-manage-access-control',
          growthbookFlagId: 'header-menu-manage-access-control',
          icon: 'eoperate-ic-access-control',
          disabled: [EPermissions.VIEW_AID, EPermissions.VIEW_EMAID_GROUPS],
        },
        {
          route: '/manage/contracts',
          name: 'HEADER.CONTRACTS',
          testId: 'header-menu-manage-contracts',
          growthbookFlagId: 'header-menu-manage-contracts',
          icon: 'eoperate-ic-emaid-rfid-management',
          // Legacy page: only existing customers (VIEW_LEGACY_EMAID) see it.
          hidden: [EPermissions.VIEW_LEGACY_EMAID],
        },
        {
          route: '/manage/voucher',
          name: 'HEADER.VOUCHER',
          testId: 'header-menu-manage-voucher',
          growthbookFlagId: 'header-menu-manage-voucher',
          icon: 'eoperate-ic-voucher-management',
          disabled: [EPermissions.VIEW_VOUCHER_CPO],
        },
        {
          hidden: [EPermissions.VIEW_FIRMWARE],
          route: '/manage/firmwareRollout',
          name: 'HEADER.FIRMWARE',
          testId: 'header-menu-manage-firmware',
          growthbookFlagId: 'header-menu-manage-firmware',
          icon: 'eoperate-ic-firmware',
        },
        {
          route: '/manage/sites',
          name: 'SITE.MENU_TITLE',
          testId: 'header-menu-manage-sites',
          growthbookFlagId: 'header-menu-manage-sites',
          icon: 'map-outlined',
          disabled: [EPermissions.VIEW_SITES],
        },
      ],
    },
    {
      name: 'HEADER.DRIVER_AND_FLEET',
      route: '/driverAndFleet',
      testId: 'header-menu-driver-and-fleet',
      growthbookFlagId: 'header-menu-driver-and-fleet',
      icon: 'car-smart-outlined',
      hidden: [
        EPermissions.VIEW_FLEET_ACCOUNTS,
        EPermissions.VIEW_EV_DRIVER_ACCOUNTS,
        EPermissions.VIEW_USERCHARGING_CONFIGURATION,
        EPermissions.VIEW_CPO_USERCHARGING_TARIFFS,
        EPermissions.VIEW_INVITED_USERCHARGING_EVDRIVER,
      ],
      links: [
        {
          route: '/driverAndFleet/fleetCharging',
          name: 'HEADER.FLEET_CHARGING',
          testId: 'header-menu-driver-and-fleet-fleet-charging',
          growthbookFlagId: 'header-menu-driver-and-fleet-fleet-charging',
          icon: 'activity-dashboard-outlined',
          hidden: [EPermissions.VIEW_FLEET_ACCOUNTS],
        },
        {
          route: '/driverAndFleet/userCharging',
          routeParams: { activeTab: '0' },
          name: 'HEADER.USER_CHARGING',
          testId: 'header-menu-driver-and-fleet-user-charging',
          growthbookFlagId: 'header-menu-driver-and-fleet-usr-charging',
          icon: 'eoperate-ic-charging-infrastructure',
          hidden: [
            EPermissions.VIEW_USERCHARGING_CONFIGURATION,
            EPermissions.VIEW_CPO_USERCHARGING_TARIFFS,
            EPermissions.VIEW_INVITED_USERCHARGING_EVDRIVER,
          ],
        },
        {
          route: '/driverAndFleet/evDriverManagement',
          name: 'HEADER.OWN_DRIVER',
          testId: 'header-menu-driver-and-fleet-ev-driver-management',
          growthbookFlagId: 'header-menu-driver-and-fleet-ev-driver-management',
          icon: 'eoperate-ic-charging-records',
          hidden: [EPermissions.VIEW_EV_DRIVER_ACCOUNTS],
        },
      ],
    },
    {
      name: 'HEADER.PAY',
      route: '/pay',
      testId: 'header-menu-pay',
      growthbookFlagId: 'header-menu-pay',
      icon: 'eoperate-ic-payments',
      hidden: [
        EPermissions.VIEW_CPO_TARIFFS,
        EPermissions.VIEW_CPO_TARIFF_ASSIGNMENT,
        EPermissions.VIEW_EMP_TARIFFS,
        EPermissions.VIEW_EMP_TARIFF_ASSIGNMENT,
        EPermissions.VIEW_DIRECT_PAYMENT_SETTINGS,
        EPermissions.VIEW_INCREASE_REVENUE,
        EPermissions.VIEW_PAYMENT_TERMINALS,
        EPermissions.VIEW_PAYMENT_TERMINAL_RECEIPTS,
      ],
      links: [
        {
          route: '/pay/cpo-price-groups',
          name: 'HEADER.CPO_PRICES',
          testId: 'header-menu-pay-cpo-price-groups',
          growthbookFlagId: 'header-menu-pay-cpo-price-groups',
          icon: 'eoperate-ic-manage-cpo-tariffs',
          hidden: [EPermissions.VIEW_CPO_TARIFFS],
        },
        {
          route: '/pay/reseller-prices',
          name: 'HEADER.CPO_RESELLER_PRICES',
          testId: 'header-menu-pay-reseller-prices',
          growthbookFlagId: 'header-menu-pay-reseller-prices',
          icon: 'ic-reseller-prices',
          hidden: [EPermissions.VIEW_CPO_RESELLER_TARIFFS],
        },
        {
          route: '/pay/subscription',
          name: 'HEADER.MANAGE_EV_TARIFFS',
          testId: 'header-menu-pay-subscription',
          growthbookFlagId: 'header-menu-pay-subscription',
          icon: 'eoperate-ic-manage-ev-tariffs',
          hidden: [EPermissions.VIEW_EMP_TARIFFS],
        },
        {
          route: '/pay/empAssignments',
          routeParams: { activeTab: '0' },
          name: 'HEADER.ASSIGN_EV_TARIFFS',
          testId: 'header-menu-pay-emp-assignment',
          growthbookFlagId: 'header-menu-pay-emp-assignment',
          icon: 'eoperate-ic-assign-ev-tariffs',
          hidden: [EPermissions.VIEW_EMP_TARIFF_ASSIGNMENT],
        },
        {
          route: '/pay/mdp',
          name: 'HEADER.MDP',
          testId: 'header-menu-pay-mdp',
          growthbookFlagId: 'header-menu-pay-mdp',
          icon: 'eoperate-ic-direct-payment',
          disabled: [EPermissions.VIEW_DIRECT_PAYMENT_SETTINGS],
        },
        {
          route: '/pay/increaseRevenue',
          name: 'HEADER.INCREASE_REVENUE',
          testId: 'header-menu-pay-increase-revenue',
          growthbookFlagId: 'header-menu-pay-increase-revenue',
          icon: 'eoperate-ic-increase-revenue',
          disabled: [EPermissions.VIEW_INCREASE_REVENUE],
        },
        {
          route: '/pay/terminalManagement',
          name: 'HEADER.TERMINAL_MANAGEMENT',
          testId: 'header-menu-pay-terminal-management',
          growthbookFlagId: 'header-menu-pay-terminal-management',
          icon: 'eoperate-ic-payment-terminal',
          disabled: [EPermissions.VIEW_PAYMENT_TERMINALS],
        },
        {
          route: '/pay/receipts',
          name: 'HEADER.RECEIPTS',
          testId: 'header-menu-pay-receipts',
          growthbookFlagId: 'header-menu-pay-receipts',
          icon: 'bill-outlined',
          disabled: [EPermissions.VIEW_PAYMENT_TERMINAL_RECEIPTS],
        },
      ],
    },
    {
      name: 'HEADER.CLEARING_HOUSE',
      route: '/clearing',
      testId: 'header-menu-clearing',
      growthbookFlagId: 'header-menu-clearing',
      icon: 'eoperate-ic-clearing-house',
      hidden: [
        EPermissions.VIEW_CLH_TARIFFS,
        EPermissions.VIEW_ROAMING_OFFERS,
        EPermissions.VIEW_BDR,
        EPermissions.MANAGE_BDR_CORRECTION,
      ],
      links: [
        {
          route: '/clearing/clearinghouse-tariffs',
          name: 'HEADER.MANAGE_CLEARINGHOUSE_TARIFFS',
          testId: 'header-menu-clearing-clearinghouse-tariffs',
          growthbookFlagId: 'header-menu-clearing-clearinghouse-tariffs',
          icon: 'eoperate-ic-clearing-house',
          hidden: [EPermissions.VIEW_CLH_TARIFFS],
        },
        {
          route: '/clearing/roaming-offers',
          name: 'HEADER.MANAGE_ROAMING_OFFERS',
          testId: 'header-menu-clearing-roaming-offers',
          growthbookFlagId: 'header-menu-clearing-roaming-offers',
          icon: 'eoperate-ic-manage-roaming-offers',
          hidden: [EPermissions.VIEW_ROAMING_OFFERS],
        },
        {
          route: '/clearing/bdr-management',
          name: 'HEADER.BDR_MANAGEMENT',
          testId: 'header-menu-clearing-bdr-management',
          growthbookFlagId: 'header-menu-clearing-bdr-management',
          icon: 'eoperate-ic-bdr-management',
          disabled: [EPermissions.VIEW_BDR, EPermissions.MANAGE_BDR_CORRECTION],
        },
      ],
    },
    {
      name: 'HEADER.PORTAL',
      route: '/portalManagement',
      testId: 'header-menu-portal',
      growthbookFlagId: 'header-menu-portal',
      icon: 'eoperate-ic-portal',
      hidden: [
        EPermissions.VIEW_CLIENT_USER_MANAGEMENT,
        EPermissions.VIEW_GLOBAL_USER_MANAGEMENT,
        EPermissions.VIEW_LOCATION_MANAGEMENT,
        EPermissions.MANAGE_CLIENT_USER_MANAGEMENT,
        EPermissions.MANAGE_GLOBAL_USER_MANAGEMENT,
        EPermissions.MANAGE_LOGIN_MESSAGES,
        EPermissions.MANAGE_POI_SERVICE_PLATFORM_PROV,
        EPermissions.VIEW_ROAMING_RELATIONSHIPS,
        EPermissions.MANAGE_ROAMING_RELATIONSHIPS,
        EPermissions.VIEW_STATION_CATALOG,
        EPermissions.MANAGE_STATION_CATALOG,
        EPermissions.VIEW_PORTAL_BRANDING,
      ],
      links: [
        {
          route: '/portalManagement/portalUsers',
          routeParams: { activeTab: '0' },
          name: 'HEADER.USER_MGT',
          testId: 'header-menu-portal-user-management',
          growthbookFlagId: 'header-menu-portal-user-management',
          icon: 'eoperate-ic-portal-user',
          disabled: [
            EPermissions.VIEW_CLIENT_USER_MANAGEMENT,
            EPermissions.VIEW_GLOBAL_USER_MANAGEMENT,
          ],
        },
        {
          route: '/portalManagement/locations',
          name: 'HEADER.LOCATION_MGT',
          testId: 'header-menu-portal-locations',
          growthbookFlagId: 'header-menu-portal-locations',
          icon: 'eoperate-ic-location-management',
          disabled: [EPermissions.VIEW_LOCATION_MANAGEMENT],
        },
        {
          checkIfInternalRole: true,
          route: '/portalManagement/clientAccountManagement',
          name: 'HEADER.CLIENT_ACCOUNT_MANAG',
          testId: 'header-menu-portal-client-accounts',
          growthbookFlagId: 'header-menu-portal-client-accounts',
          icon: 'eoperate-ic-client-account-management',
          disabled: [
            EPermissions.MANAGE_CLIENT_USER_MANAGEMENT,
            EPermissions.MANAGE_GLOBAL_USER_MANAGEMENT,
          ],
        },
        {
          checkIfInternalRole: true,
          route: '/portalManagement/loginMessages',
          name: 'HEADER.LOGIN_MESSAGES',
          testId: 'header-menu-portal-login-messages',
          growthbookFlagId: 'header-menu-portal-login-messages',
          icon: 'eoperate-ic-login-messages',
          disabled: [EPermissions.MANAGE_LOGIN_MESSAGES],
        },
        {
          checkIfInternalRole: true,
          route: '/portalManagement/webservices',
          name: 'HEADER.WEBSERVICES',
          testId: 'header-menu-portal-webservices',
          growthbookFlagId: 'header-menu-portal-webservices',
          icon: 'eoperate-ic-webservice-feed-endpoints',
          disabled: [EPermissions.MANAGE_POI_SERVICE_PLATFORM_PROV],
        },
        {
          checkIfInternalRole: true,
          route: '/portalManagement/roamingRelationships',
          routeParams: { activeTab: '0' },
          name: 'HEADER.ROAMING_RELATIONSHIPS',
          testId: 'header-menu-portal-roaming-relationships',
          growthbookFlagId: 'header-menu-portal-roaming-relationships',
          icon: 'eoperate-ic-roaming-relations',
          disabled: [
            EPermissions.VIEW_ROAMING_RELATIONSHIPS,
            EPermissions.MANAGE_ROAMING_RELATIONSHIPS,
          ],
        },
        {
          route: '/portalManagement/branding',
          name: 'HEADER.BRANDING',
          testId: 'header-menu-portal-branding',
          growthbookFlagId: 'header-menu-portal-branding',
          icon: 'edit-1-outlined',
          hidden: [EPermissions.VIEW_PORTAL_BRANDING],
        },
        {
          checkIfInternalRole: true,
          route: '/portalManagement/stationCatalog',
          name: 'HEADER.STATION_CATALOG',
          testId: 'header-menu-portal-station-catalog',
          growthbookFlagId: 'header-menu-portal-station-catalog',
          icon: 'eoperate-ic-station-management',
          hidden: [EPermissions.VIEW_STATION_CATALOG, EPermissions.MANAGE_STATION_CATALOG],
        },
      ],
    },
    {
      name: 'HEADER.BUSINESS_PARTNER',
      route: '/businessPartner',
      testId: 'header-menu-business-partner',
      growthbookFlagId: 'header-menu-business-partner',
      icon: 'eoperate-ic-business-partner',
      hidden: [
        EPermissions.VIEW_SUB_BUSINESS_PARTNER,
        EPermissions.VIEW_PRODUCT_ASSIGNMENT,
        EPermissions.MANAGE_VOUCHERS,
        EPermissions.VIEW_BP_MASTERDATA,
      ],
      links: [
        {
          route: '/businessPartner/subBusinessPartner',
          name: 'HEADER.MANAGE_BUSINESS_PARTNER',
          testId: 'header-menu-business-partner-manage-partners',
          growthbookFlagId: 'header-menu-business-partner-manage-partners',
          icon: 'eoperate-ic-business-partner-management',
          disabled: [EPermissions.VIEW_SUB_BUSINESS_PARTNER],
        },
        {
          route: '/businessPartner/contractedProducts',
          name: 'HEADER.PRODUCT_ASSIGNMENT',
          testId: 'header-menu-business-partner-product-assignment',
          growthbookFlagId: 'header-menu-business-partner-product-assignment',
          icon: 'eoperate-ic-assignment',
          disabled: [EPermissions.VIEW_PRODUCT_ASSIGNMENT],
        },
        {
          route: '/businessPartner/productLicense',
          name: 'HEADER.PRODUCT_LICENSE',
          testId: 'header-menu-business-partner-product-voucher',
          growthbookFlagId: 'header-menu-business-partner-product-voucher',
          icon: 'eoperate-ic-voucher-management',
          hidden: [EPermissions.MANAGE_VOUCHERS],
        },
        {
          route: '/businessPartner/documents',
          name: 'HEADER.DOCUMENTS',
          testId: 'header-menu-business-partner-documents',
          growthbookFlagId: 'header-menu-business-partner-documents',
          icon: 'menu-outlined-2',
          hidden: [EPermissions.VIEW_DOCUMENTS],
        },
        {
          route: '/businessPartner/masterData',
          name: 'HEADER.BP_MASTER_DATA',
          testId: 'header-menu-business-partner-master-data',
          growthbookFlagId: 'header-menu-business-partner-master-data',
          icon: 'data-2-outlined',
          hidden: [EPermissions.VIEW_BP_MASTERDATA],
        },
      ],
    },
  ],
};

/**
 * Returns a defensive copy of the menu structure.
 *
 * Visibility contract (see `MenuGroupComponent.isVisible`): every link is
 * resolved through GrowthBook via
 * `FeatureFlagService.isMenuItemVisible(link.growthbookFlagId)`, which is
 * fail-closed — a flag that is not in the GrowthBook payload hides the link.
 * A section (parent menu item) is shown only when its own
 * `section.growthbookFlagId` is on AND at least one of its links is visible.
 *
 * Flag evaluation deliberately happens in the component (not here) so that it
 * re-runs on every `FeatureFlagService.changes$` emission (SSE updates).
 */
export function getMenuPoints(): MenuPoints {
  return {
    sections: MENU_POINTS.sections.map(section => ({
      ...section,
      links: section.links.map(link => ({ ...link })),
    })),
  };
}
